import { WorkflowApiGetLatestRequest, WorkflowRead } from '@ariksa/awm/api';
import { TagsApiCreateTagsRequest } from '@ariksa/inventory-core';
import {
  AppSchemasInventoryResourceCountDeploymentSummary,
  Catalogue,
  InsightV2ApiInsightV2Request,
  InsightV2Pagination,
  InventorySummary,
  ResourceApiGetDeploymentSummaryRequest,
  ResourceApiGetInventoryOverviewRequest,
  ResourceApiGetInventorySummaryRequest,
  ResourceApiGetVpcsRequest,
  ResourceAnalyticsApiGetResourceAnalyticsDeploymentSummaryRequest,
  ResourceAnalyticsApiGetResourceAnalyticsInventoryCountRequest,
  ResourceAnalyticsApiGetResourceAnalyticsOverviewRequest,
  ResourceAnalyticsApiGetResourceAnalyticsSummaryRequest,
  ResourceAnalyticsApiGetResourceAnalyticsVpcsRequest,
  ResourceApiGetInventoryCatalogueRequest,
  ResourceVulnerabilities,
  TagsApiGetTagsResourceRequest,
  TagsApiRemoveTagRequest,
  TagsRead,
  VPCInfo,
  VulnerabilitiesApiGetVulnerabilitiesRequest,
} from '@ariksa/inventory-core/api';
import { AllResourceCountResponse } from '@ariksa/reporting/api';
import { call } from 'redux-saga/effects';
import { QueryContext } from 'services/utils/QueryContext';
import { takeLatestAction } from 'services/utils/takeLatestAction';

import { getInventoryResourceQuery } from 'api/inventory/resource-query';
import {
  getScanVaultVulnerabilityResourceInsight,
  VulnerabilityResourceInsightResponse,
} from 'api/scan-vault/client';
import {
  InventoryService,
  ReportingService,
  WorkflowService,
} from 'api/services';

import { actions } from './slice';

export function* inventorySaga() {
  yield takeLatestAction(
    actions.getResourceCategories,
    doGetResourceCategories,
  );
  yield takeLatestAction(actions.getInstanceInventory, doGetInstanceInventory);
  yield takeLatestAction(actions.createTags, doCreateTags);
  yield takeLatestAction(actions.detachContext, doDetachContext);
  yield takeLatestAction(actions.getContextValues, doGetContextValues);
  yield takeLatestAction(actions.getAttachedContexts, doGetAttachedContexts);
  yield takeLatestAction(actions.getLastRefreshed, doGetLastRefreshed);
  yield takeLatestAction(actions.getDeploymentSummary, doGetDeploymentSummary);
  yield takeLatestAction(actions.getVulnerabilities, doGetVulnerabilities);
  yield takeLatestAction(actions.getInventoryOverview, doGetInventoryOverview);
  yield takeLatestAction(actions.getInventoryHistory, doGetInventoryHistory);
  yield takeLatestAction(actions.getVPCs, doGetVPCs);
}

export function* doGetResourceCategories(
  ctx: QueryContext<
    InventorySummary,
    | ResourceApiGetInventorySummaryRequest
    | ResourceAnalyticsApiGetResourceAnalyticsSummaryRequest
  >,
) {
  const request = ctx.params as any;
  yield call(
    ctx.fetch,
    () =>
      request.useInventoryV3
        ? InventoryService.ResourceAnalytics.getResourceAnalyticsSummary(
            request,
          )
        : InventoryService.Resource.getInventorySummary(request),
    {
      errorMsg: 'Failed to get resources categories!',
      cacheKey: 'inventoryResourceCategories',
    },
  );
}

export function* doGetVPCs(
  ctx: QueryContext<
    VPCInfo[],
    | ResourceApiGetVpcsRequest
    | ResourceAnalyticsApiGetResourceAnalyticsVpcsRequest
  >,
) {
  const request = ctx.params as any;
  yield call(
    ctx.fetch,
    () =>
      request.useInventoryV3
        ? InventoryService.ResourceAnalytics.getResourceAnalyticsVpcs(request)
        : InventoryService.Resource.getVpcs(request),
    {
      errorMsg: 'Failed to get VPCs!',
      cacheKey: 'vpcList',
    },
  );
}

export function* doCreateTags(
  ctx: QueryContext<any, TagsApiCreateTagsRequest>,
) {
  yield call(ctx.fetch, () => InventoryService.Tags.createTags(ctx.params));
}

export function* doGetAttachedContexts(
  ctx: QueryContext<TagsRead[], TagsApiGetTagsResourceRequest>,
) {
  yield call(ctx.fetch, () =>
    InventoryService.Tags.getTagsResource(ctx.params),
  );
}

export function* doDetachContext(
  ctx: QueryContext<any, TagsApiRemoveTagRequest>,
) {
  yield call(ctx.fetch, () => InventoryService.Tags.removeTag(ctx.params));
}

export function* doGetContextValues(
  ctx: QueryContext<Catalogue[], ResourceApiGetInventoryCatalogueRequest>,
) {
  yield call(ctx.fetch, () =>
    InventoryService.Resource.getInventoryCatalogue(ctx.params),
  );
}

export function* doGetInstanceInventory(
  ctx: QueryContext<InsightV2Pagination, InsightV2ApiInsightV2Request>,
) {
  const request = (ctx.params as any)?.insightV2Request ?? (ctx.params as any);
  if ((ctx.params as any)?.useInventoryV3) {
    yield call(
      ctx.fetch as any,
      () =>
        getInventoryResourceQuery({
          environment_id: request.environment_id,
          account_id: request.account_id,
          native_type: request.resource_type
            ? [request.resource_type]
            : undefined,
          category: request.category ? [request.category] : undefined,
          region: request.regions,
          vpcs: request.vpcs,
          search_term: request.search_term,
          page: request.page,
          size: request.size,
        }),
      {
        map: value => ({
          items: value.items.map(item => ({
            ...item,
            uuid: item.uuid ?? item.resource_uuid,
            entity_uuid: item.entity_uuid ?? item.resource_uuid,
            resource_id: item.resource_id ?? item.native_id,
            id: item.id ?? item.native_id,
            account: item.account ?? item.account_id ?? item.account_native_id,
            cloud: item.cloud ?? item.provider,
            native_name: item.native_name ?? item.native_type,
          })),
          total: value.total,
          page: value.page,
          size: value.size,
        }),
      },
    );
    return;
  }
  yield call(ctx.fetch, () => InventoryService.InsightV2.insightV2(ctx.params));
}

export function* doGetDeploymentSummary(
  ctx: QueryContext<
    AppSchemasInventoryResourceCountDeploymentSummary[],
    | ResourceApiGetDeploymentSummaryRequest
    | ResourceAnalyticsApiGetResourceAnalyticsDeploymentSummaryRequest
  >,
) {
  const request = ctx.params as any;
  yield call(
    ctx.fetch,
    () =>
      request.useInventoryV3
        ? InventoryService.ResourceAnalytics.getResourceAnalyticsDeploymentSummary(
            request,
          )
        : InventoryService.Resource.getDeploymentSummary(request),
    {
      errorMsg: 'Failed to get deployment summary!',
    },
  );
}

export function* doGetLastRefreshed(
  ctx: QueryContext<WorkflowRead[], WorkflowApiGetLatestRequest>,
) {
  yield call(ctx.fetch, () => WorkflowService.getLatest(ctx.params));
}

export function* doGetVulnerabilities(
  ctx: QueryContext<
    ResourceVulnerabilities[],
    VulnerabilitiesApiGetVulnerabilitiesRequest
  >,
) {
  const params = ctx.params as any;
  if (params?.useScanVaultVulnerabilityFindings) {
    const request = params.resourceVulnerabilitiesRequest ?? {};
    const resourceUUIDs = (request.resource_ids ?? []).filter(Boolean);
    if (!resourceUUIDs.length) {
      yield call(ctx.fetch as any, async () => [], { map: value => value });
      return;
    }
    yield call(
      ctx.fetch as any,
      () =>
        getScanVaultVulnerabilityResourceInsight({
          resource_uuids: resourceUUIDs,
          account_ids: request.account_ids?.length
            ? request.account_ids
            : undefined,
          environment_ids: request.environment_id
            ? [request.environment_id]
            : undefined,
          status: 'open',
          size: resourceUUIDs.length,
        }),
      {
        map: mapScanVaultResourceInsightToInventory,
        errorMsg: 'Failed to get vulnerabilities!',
      },
    );
    return;
  }
  yield call(
    ctx.fetch,
    () => InventoryService.Vulnerability.getVulnerabilities(ctx.params),
    { errorMsg: 'Failed to get vulnerabilities!' },
  );
}

// Shapes ScanVault resource insight rows like the legacy inventory
// vulnerability summary the instance table renders.
export function mapScanVaultResourceInsightToInventory(
  response: VulnerabilityResourceInsightResponse,
): ResourceVulnerabilities[] {
  return (response?.items ?? []).map(item => ({
    resource_id: item.resource_uuid,
    os_name: item.attrs?.os_name ?? null,
    os_version: item.attrs?.os_version ?? null,
    applications: item.applications ?? [],
    vulnerabilities: {
      critical: item.vulnerabilities?.critical ?? 0,
      high: item.vulnerabilities?.high ?? 0,
      medium: item.vulnerabilities?.medium ?? 0,
      low: item.vulnerabilities?.low ?? 0,
      unknown: item.vulnerabilities?.unknown ?? 0,
    },
    last_seen: item.vulnerability_last_seen_at as any,
    status: item.status,
  }));
}

export function* doGetInventoryOverview(
  ctx: QueryContext<
    AppSchemasInventoryResourceCountDeploymentSummary[],
    | ResourceApiGetInventoryOverviewRequest
    | ResourceAnalyticsApiGetResourceAnalyticsOverviewRequest
  >,
) {
  const request = ctx.params as any;
  yield call(
    ctx.fetch,
    () =>
      request.useInventoryV3
        ? InventoryService.ResourceAnalytics.getResourceAnalyticsOverview(
            request,
          )
        : InventoryService.Resource.getInventoryOverview(request),
    {
      errorMsg: 'Failed to get inventory overview!',
      cacheKey: 'inventoryOverview',
    },
  );
}

export function* doGetInventoryHistory(
  ctx: QueryContext<
    AllResourceCountResponse[],
    ResourceAnalyticsApiGetResourceAnalyticsInventoryCountRequest
  >,
) {
  const request = ctx.params as any;
  yield call(
    ctx.fetch,
    () =>
      request.useInventoryV3
        ? InventoryService.ResourceAnalytics.getResourceAnalyticsInventoryCount(
            request,
          )
        : ReportingService.Inventory.getAllResourceCount(request),
    {
      errorMsg: 'Failed to get inventory resource count and change!',
      cacheKey: 'inventoryResourceCountAndChange',
    },
  );
}
